import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, Facebook, Clock, MapPin, ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import { FadeUp } from "@/components/Reveal";
import { QuoteButton } from "@/components/Layout";
import { BUSINESS, SERVICE_AREAS } from "@/content/siteContent";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CARDS = [
  { icon: Phone, label: "Call or text", value: BUSINESS.phone, href: BUSINESS.phoneHref, testId: "contact-phone-link" },
  { icon: Mail, label: "Email", value: BUSINESS.email, href: BUSINESS.emailHref, testId: "contact-email-link" },
  { icon: Facebook, label: "Facebook", value: "Message us anytime", href: BUSINESS.facebookUrl, testId: "contact-facebook-link" },
  { icon: Clock, label: "Hours", value: BUSINESS.hours, href: null, testId: "contact-hours" },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email) || !form.message.trim()) {
      toast.error("Please fill in your name, a valid email, and a message.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent — we'll get back to you soon.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send. Please call or email us directly.");
    } finally {
      setSending(false);
    }
  };

  const inputCls = "w-full border border-white/10 bg-navy-900 px-4 py-3 text-sm text-slate-100 placeholder:text-slate-500 transition-colors focus:border-gold focus:outline-none";

  return (
    <>
      <Seo
        title="Contact Us | Home Team Moving Co."
        siteName="Home Team Moving Co."
        description="Get in touch with Home Team Moving Co. — veteran-owned moving and hauling across Southern California. Call, email, or message us."
      />
      <section className="border-b border-white/10">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:py-24">
          <FadeUp>
            <p className="eyebrow mb-4">Contact</p>
            <h1 className="max-w-3xl font-display text-4xl font-extrabold uppercase leading-none tracking-tight text-white sm:text-5xl lg:text-6xl">
              Talk to <span className="text-gold">the team.</span>
            </h1>
            <p className="mt-5 max-w-2xl leading-relaxed text-slate-300">
              Questions, scheduling, or a job that doesn't fit a box — reach us any way you like. For the fastest estimate, use the quote form and attach photos.
            </p>
          </FadeUp>
          <div className="mt-12 grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-4">
            {CARDS.map((c, i) => (
              <FadeUp key={c.label} delay={i * 0.08} className="bg-navy-950 p-7">
                <c.icon className="h-6 w-6 text-gold" />
                <p className="mt-4 text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-500">{c.label}</p>
                {c.href ? (
                  <a href={c.href} data-testid={c.testId} target={c.href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" className="mt-1 block break-all font-display text-lg font-bold uppercase tracking-wide text-white transition-colors hover:text-gold">
                    {c.value}
                  </a>
                ) : (
                  <p data-testid={c.testId} className="mt-1 font-display text-lg font-bold uppercase tracking-wide text-white">{c.value}</p>
                )}
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-white/10">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 py-16 sm:px-6 lg:grid-cols-2 lg:py-24">
          <FadeUp>
            <h2 className="font-display text-2xl font-bold uppercase tracking-tight text-white sm:text-3xl">Send a message</h2>
            <form data-testid="contact-form" onSubmit={submit} className="mt-8 space-y-5">
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="contact-name" className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Name *</label>
                  <input id="contact-name" data-testid="contact-name-input" className={inputCls} value={form.name} onChange={set("name")} autoComplete="name" />
                </div>
                <div>
                  <label htmlFor="contact-email" className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Email *</label>
                  <input id="contact-email" data-testid="contact-email-input" type="email" className={inputCls} value={form.email} onChange={set("email")} autoComplete="email" />
                </div>
              </div>
              <div>
                <label htmlFor="contact-phone" className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Phone</label>
                <input id="contact-phone" data-testid="contact-phone-input" type="tel" className={inputCls} value={form.phone} onChange={set("phone")} autoComplete="tel" />
              </div>
              <div>
                <label htmlFor="contact-message" className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Message *</label>
                <textarea id="contact-message" data-testid="contact-message-input" rows={5} className={inputCls} value={form.message} onChange={set("message")} placeholder="How can we help?" />
              </div>
              <button
                type="submit"
                data-testid="contact-submit-btn"
                disabled={sending}
                className="inline-flex items-center gap-2 bg-gold px-8 py-3.5 font-display text-sm font-bold uppercase tracking-[0.2em] text-navy-950 transition-colors hover:bg-gold-light disabled:opacity-60"
              >
                {sending ? "Sending…" : "Send message"} <ArrowRight className="h-4 w-4" />
              </button>
            </form>
          </FadeUp>
          <FadeUp delay={0.15}>
            <h2 className="font-display text-2xl font-bold uppercase tracking-tight text-white sm:text-3xl">Where we work</h2>
            <p className="mt-4 text-sm leading-relaxed text-slate-300">
              Based in Southern California and rolling daily across the region. Outside these areas? Ask — we're flexible for the right job.
            </p>
            <div data-testid="service-area-map" className="mt-6 flex flex-wrap gap-3">
              {SERVICE_AREAS.map((area) => (
                <span key={area} className="inline-flex items-center gap-2 border border-white/10 bg-navy-900 px-4 py-2.5 font-display text-xs font-semibold uppercase tracking-[0.15em] text-slate-200">
                  <MapPin className="h-3.5 w-3.5 text-gold" /> {area}
                </span>
              ))}
            </div>
            <div className="mt-10 border border-gold/30 bg-navy-900/60 p-8">
              <h3 className="font-display text-xl font-bold uppercase tracking-wide text-white">Need an estimate fast?</h3>
              <p className="mt-2 text-sm text-slate-300">Skip the back-and-forth — send photos of the job and get a price back.</p>
              <div className="mt-6">
                <QuoteButton testId="contact-quote-cta" />
              </div>
            </div>
          </FadeUp>
        </div>
      </section>
    </>
  );
}
